// Process Flow Data for Peak Priorities Elevation
// Defines the 6-phase verb-based journey from idea to monetized products/services

export interface Operation {
  id: string;
  name: string;
  description: string;
  inputSchema: string;
  outputSchema: string;
  example: string;
}

export interface Engine {
  id: string;
  name: string;
  purpose: string;
  operations: Operation[];
}

export interface ProcessPhase {
  id: string;
  verb: string;
  title: string;
  description: string;
  engines: Engine[];
  icon: string;
  color: string;
  details: string;
  examples: string[];
}

export const processPhases: ProcessPhase[] = [
  {
    id: 'gathering',
    verb: 'Gathering',
    title: 'Gathering Ideas & Content',
    description: 'Collecting ideas, content, and insights from diverse sources',
    engines: [
      {
        id: 'prospector_engine',
        name: 'Prospector Engine',
        purpose: 'Discovers and captures content from various sources',
        operations: [
          {
            id: 'ocr_text',
            name: 'ocr_text',
            description: 'Extract text from text-based documents and files',
            inputSchema: 'text_file | document_file',
            outputSchema: 'extracted_text',
            example: 'ocr_text("document.txt") → "Extracted text content from document"'
          },
          {
            id: 'ocr_image',
            name: 'ocr_image',
            description: 'Extract text from images using optical character recognition',
            inputSchema: 'image_file (jpg, png, pdf)',
            outputSchema: 'extracted_text',
            example: 'ocr_image("screenshot.png") → "Text extracted from image"'
          },
          {
            id: 'ocr_notion_content',
            name: 'ocr_notion_content',
            description: 'Extract and process content from Notion pages and databases',
            inputSchema: 'notion_page_url | notion_database_id',
            outputSchema: 'processed_notion_content',
            example: 'ocr_notion_content("https://notion.so/page") → {title: "Page Title", content: "..."}'
          },
          {
            id: 'sync_dropbox_notion',
            name: 'sync_dropbox_notion',
            description: 'Sync files from Dropbox to Notion (3rd party service)',
            inputSchema: 'dropbox_folder_path, notion_database_id',
            outputSchema: 'sync_status',
            example: 'sync_dropbox_notion("/Documents", "notion_db_id") → {synced: 5, failed: 0}'
          },
          {
            id: 'sync_googledrive_notion',
            name: 'sync_googledrive_notion',
            description: 'Sync files from Google Drive to Notion (3rd party service)',
            inputSchema: 'google_drive_folder_id, notion_database_id',
            outputSchema: 'sync_status',
            example: 'sync_googledrive_notion("drive_folder_id", "notion_db_id") → {synced: 3, failed: 0}'
          },
          {
            id: 'send_chatgpt_to_notion',
            name: 'send_chatgpt_to_notion',
            description: 'Send ChatGPT conversation history to Notion (3rd party service)',
            inputSchema: 'chatgpt_conversation_id, notion_database_id',
            outputSchema: 'notion_page_url',
            example: 'send_chatgpt_to_notion("chat_123", "notion_db_id") → "https://notion.so/page_456"'
          }
        ]
      }
    ],
    icon: 'Inbox',
    color: 'blue',
    details: 'The foundation phase where we discover and capture raw materials - ideas, content, insights, and opportunities from various sources including research, client interactions, market intelligence, and internal knowledge.',
    examples: [
      'Research articles and industry reports',
      'Client feedback and insights',
      'Market trends and opportunities',
      'Internal knowledge and expertise'
    ]
  },
  {
    id: 'tagging',
    verb: 'Tagging',
    title: 'Tagging & Categorizing',
    description: 'Adding metadata, categorization, and structure to collected content',
    engines: [
      {
        id: 'knowledge_engine',
        name: 'Knowledge Engine',
        purpose: 'Adds metadata, categorizes, and links related materials',
        operations: [
          {
            id: 'send_to_notion_database',
            name: 'send_to_notion_database',
            description: 'Create a new page in a Notion database with structured content',
            inputSchema: 'content_object, notion_database_id, page_properties',
            outputSchema: 'notion_page_url',
            example: 'send_to_notion_database({title: "New Article", content: "..."}, "db_id", {status: "Draft"}) → "https://notion.so/page_123"'
          },
          {
            id: 'search_append_notion_page',
            name: 'search_append_notion_page',
            description: 'Search for existing Notion page and append content to it',
            inputSchema: 'search_query, content_to_append, notion_database_id',
            outputSchema: 'updated_page_url',
            example: 'search_append_notion_page("Project Alpha", "New update...", "db_id") → "https://notion.so/page_456"'
          },
          {
            id: 'auto_tag',
            name: 'auto_tag',
            description: 'Apply tags based on content classification',
            inputSchema: 'text_content',
            outputSchema: 'tags_array',
            example: 'auto_tag("AI article") → ["artificial-intelligence", "technology", "innovation"]'
          },
          {
            id: 'extract_entities',
            name: 'extract_entities',
            description: 'Identify named entities and relationships',
            inputSchema: 'text_content',
            outputSchema: 'entities_object',
            example: 'extract_entities("John works at OpenAI") → {person: "John", organization: "OpenAI", relationship: "works_at"}'
          }
        ]
      }
    ],
    icon: 'Tag',
    color: 'green',
    details: 'The organization phase where we apply our meta-taxonomy framework to classify, tag, and structure all collected content. This creates the foundation for intelligent retrieval and relationship mapping.',
    examples: [
      'Applying category and structure metadata',
      'Creating relationship mappings',
      'Establishing content hierarchies',
      'Building searchable taxonomies'
    ]
  },
  {
    id: 'formulating',
    verb: 'Formulating',
    title: 'Research & Development',
    description: 'Research, refinement, and development of ideas into coherent frameworks',
    engines: [
      {
        id: 'curation_engine',
        name: 'Curation Engine',
        purpose: 'Facilitates team review and refinement of content',
        operations: [
          {
            id: 'review_content',
            name: 'review_content',
            description: 'Enable collaborative review and feedback on content',
            inputSchema: 'content_object, reviewer_id',
            outputSchema: 'review_feedback',
            example: 'review_content(article, "user123") → {feedback: "Great structure", suggestions: [...]}'
          },
          {
            id: 'refine_framework',
            name: 'refine_framework',
            description: 'Iteratively improve frameworks based on feedback',
            inputSchema: 'framework_object, feedback_array',
            outputSchema: 'refined_framework',
            example: 'refine_framework(framework, feedback) → {improved_structure: true, clarity_score: 0.95}'
          }
        ]
      },
      {
        id: 'canon_engine',
        name: 'Canon Engine',
        purpose: 'Establishes authoritative source of truth for content',
        operations: [
          {
            id: 'establish_canon',
            name: 'establish_canon',
            description: 'Mark content as canonical and authoritative',
            inputSchema: 'content_object',
            outputSchema: 'canonical_status',
            example: 'establish_canon(framework) → {canonical: true, version: "1.0", authority: "high"}'
          },
          {
            id: 'version_control',
            name: 'version_control',
            description: 'Track and manage content versions',
            inputSchema: 'content_object',
            outputSchema: 'version_info',
            example: 'version_control(article) → {current_version: "2.1", history: [...]}'
          }
        ]
      }
    ],
    icon: 'FlaskConical',
    color: 'purple',
    details: 'The development phase where we research, refine, and develop ideas into coherent frameworks, methodologies, and structured knowledge. This is where raw content becomes valuable intellectual property.',
    examples: [
      'Developing frameworks and methodologies',
      'Creating structured knowledge objects',
      'Refining concepts through research',
      'Building comprehensive models'
    ]
  },
  {
    id: 'creating',
    verb: 'Creating',
    title: 'Authoring & Designing',
    description: 'Authoring content and designing visual assets for maximum impact',
    engines: [
      {
        id: 'authoring_engine',
        name: 'Authoring Engine',
        purpose: 'Provides tools and interfaces for content creation',
        operations: [
          {
            id: 'write_content',
            name: 'write_content',
            description: 'Generate and edit written content with AI assistance',
            inputSchema: 'topic, style, length',
            outputSchema: 'written_content',
            example: 'write_content("AI trends", "professional", 500) → "Artificial intelligence is transforming..."'
          },
          {
            id: 'structure_content',
            name: 'structure_content',
            description: 'Organize content into logical structure and hierarchy',
            inputSchema: 'raw_content',
            outputSchema: 'structured_content',
            example: 'structure_content(notes) → {title: "Guide", sections: [...], subsections: [...]}'
          }
        ]
      },
      {
        id: 'studio_engine',
        name: 'Studio Engine',
        purpose: 'Produces design-ready visuals and layouts',
        operations: [
          {
            id: 'render_visual',
            name: 'render_visual',
            description: 'Generate visual designs and graphics',
            inputSchema: 'design_brief, style_guide',
            outputSchema: 'visual_assets',
            example: 'render_visual(brief, style) → {images: [...], layouts: [...], colors: [...]}'
          },
          {
            id: 'harmonize_colors',
            name: 'harmonize_colors',
            description: 'Create cohesive color palettes and schemes',
            inputSchema: 'brand_colors, context',
            outputSchema: 'color_palette',
            example: 'harmonize_colors(primary, "professional") → {primary: "#1a365d", secondary: "#2d3748", accent: "#ed8936"}'
          },
          {
            id: 'compose_layout',
            name: 'compose_layout',
            description: 'Arrange content and visuals into final layouts',
            inputSchema: 'content_blocks, visual_assets',
            outputSchema: 'composed_layout',
            example: 'compose_layout(blocks, images) → {layout: "grid", arrangement: [...], responsive: true}'
          }
        ]
      }
    ],
    icon: 'Edit3',
    color: 'orange',
    details: 'The creation phase where we author compelling content and design visual assets that bring our knowledge to life. This combines written expertise with visual design to create powerful, engaging deliverables.',
    examples: [
      'Writing articles, guides, and documentation',
      'Creating visual designs and layouts',
      'Developing templates and frameworks',
      'Producing multimedia content'
    ]
  },
  {
    id: 'applying',
    verb: 'Applying',
    title: 'Composing & Assembling',
    description: 'Composing content with visuals into coherent, deliverable products',
    engines: [
      {
        id: 'assembly_engine',
        name: 'Assembly Engine',
        purpose: 'Composes deliverables from component parts',
        operations: [
          {
            id: 'assemble_deliverable',
            name: 'assemble_deliverable',
            description: 'Combine content and visuals into final deliverables',
            inputSchema: 'content_components, visual_assets, template',
            outputSchema: 'deliverable_object',
            example: 'assemble_deliverable(article, images, "presentation") → {slides: [...], notes: [...], handouts: [...]}'
          },
          {
            id: 'optimize_layout',
            name: 'optimize_layout',
            description: 'Optimize layouts for different formats and devices',
            inputSchema: 'layout_object, target_format',
            outputSchema: 'optimized_layout',
            example: 'optimize_layout(layout, "mobile") → {responsive: true, breakpoints: [...], performance: "optimized"}'
          }
        ]
      }
    ],
    icon: 'Layers',
    color: 'amber',
    details: 'The composition phase where we assemble content and visuals into coherent, deliverable products and services. This is where individual components become integrated solutions.',
    examples: [
      'Assembling content into presentations',
      'Creating client deliverables',
      'Building product documentation',
      'Composing training materials'
    ]
  },
  {
    id: 'publishing',
    verb: 'Publishing',
    title: 'Distributing & Monetizing',
    description: 'Publishing content across channels to create value and generate revenue',
    engines: [
      {
        id: 'publishing_engine',
        name: 'Publishing Engine',
        purpose: 'Distributes content across channels and platforms',
        operations: [
          {
            id: 'publish_to_web',
            name: 'publish_to_web',
            description: 'Publish content to websites and blogs',
            inputSchema: 'content_object, target_site',
            outputSchema: 'publication_status',
            example: 'publish_to_web(article, "blog") → {url: "https://site.com/article", status: "published", seo_score: 0.92}'
          },
          {
            id: 'distribute_social',
            name: 'distribute_social',
            description: 'Distribute content across social media platforms',
            inputSchema: 'content_object, platforms_array',
            outputSchema: 'distribution_results',
            example: 'distribute_social(post, ["twitter", "linkedin"]) → {twitter: "posted", linkedin: "scheduled", engagement: "tracking"}'
          },
          {
            id: 'generate_revenue',
            name: 'generate_revenue',
            description: 'Track and optimize content monetization',
            inputSchema: 'content_metrics, revenue_goals',
            outputSchema: 'revenue_report',
            example: 'generate_revenue(metrics, goals) → {revenue: 1250, conversion: 0.08, roi: 2.4}'
          }
        ]
      }
    ],
    icon: 'Rocket',
    color: 'red',
    details: 'The distribution phase where we publish and distribute content across multiple channels to reach our target audiences and generate value. This is where knowledge becomes monetized products and services.',
    examples: [
      'Publishing to websites and blogs',
      'Distributing through social media',
      'Creating client-specific deliverables',
      'Launching products and services'
    ]
  }
];

// Engine to Phase Mapping (for backward compatibility)
export const enginePhaseMapping: Record<string, string[]> = {
  'Prospector Engine': ['gathering'],
  'Knowledge Engine': ['tagging'],
  'Curation Engine': ['formulating'],
  'Canon Engine': ['formulating'],
  'Authoring Engine': ['creating'],
  'Studio Engine': ['creating'],
  'Assembly Engine': ['applying'],
  'Publishing Engine': ['publishing'],
  'Media Engine': ['creating', 'applying'],
  'Analytics Engine': ['gathering', 'tagging', 'publishing']
};

// Process Flow Insights
export const processInsights = {
  title: 'The Work Before the Work',
  subtitle: 'is Getting Clear',
  description: '',
  keyPoints: []
};
